/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import BucketPricingSection from './BucketPricingSection';

interface Props {
  projectId: string;
  onCreate: (data: {
    name: string;
    region: string;
    versioning: boolean;
    is_public: boolean;
    plan_type: 'starter' | 'committed';
    min_commitment_tb?: number;
  }) => void;
  onClose: () => void;
  isCreating: boolean;
  /** Prefill from the inline "Create your first bucket" form. */
  initialName?: string;
  initialPublic?: boolean;
}

export default function CreateBucketModal({ projectId, onCreate, onClose, isCreating, initialName = '', initialPublic = false }: Props) {
  const [name, setName] = useState(initialName);
  const [versioning, setVersioning] = useState(false);
  const [isPublic, setIsPublic] = useState(initialPublic);
  const [nameError, setNameError] = useState('');
  const [planType, setPlanType] = useState<'starter' | 'committed'>('starter');
  const [commitmentTB, setCommitmentTB] = useState<number>(1);

  const validateName = (v: string) => {
    if (v.length < 3 || v.length > 63) return 'Name must be 3–63 characters';
    if (!/^[a-z0-9][a-z0-9-]*[a-z0-9]$/.test(v)) return 'Lowercase letters, numbers, and hyphens only; must start and end with a letter or number';
    return '';
  };

  const handleCreate = () => {
    const err = validateName(name);
    if (err) { setNameError(err); return; }
    onCreate({
      name,
      region: 'dc-01-jsr-india',
      versioning,
      is_public: isPublic,
      plan_type: planType,
      min_commitment_tb: planType === 'committed' ? commitmentTB : undefined,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div role="dialog" aria-modal="true" aria-labelledby="create-bucket-title" className="ps-card p-6 w-full max-w-2xl my-8 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h3 id="create-bucket-title" className="m-0 text-lg font-medium">New bucket</h3>
          <button type="button" onClick={onClose} aria-label="Close" className="w-10 h-10 grid place-items-center rounded-md text-ps-muted hover:text-ps-text hover:bg-ps-hover">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-5">
          {/* Plan picker */}
          <BucketPricingSection
            projectId={projectId}
            selectedPlanType={planType}
            selectedCommitmentTB={commitmentTB}
            onSelect={(p, tb) => {
              setPlanType(p);
              if (p === 'committed' && tb !== undefined) setCommitmentTB(tb);
            }}
          />

          <div>
            <label htmlFor="bucket-name" className="ps-label">Bucket name</label>
            <input
              value={name}
              onChange={(e) => { setName(e.target.value); setNameError(''); }}
              id="bucket-name"
              placeholder="my-bucket"
              className="ps-input font-mono"
            />
            {nameError && <p className="text-xs text-red-400 mt-1">{nameError}</p>}
          </div>

          <div>
            <span className="ps-label">Region</span>
            <div className="ps-input flex items-center text-ps-muted font-mono">
              dc-01-jsr-india
            </div>
          </div>

          <div className="flex items-center justify-between py-1">
            <div>
              <p className="m-0 text-sm text-ps-text">Versioning</p>
              <p className="m-0 text-xs text-ps-subtle">Keep multiple versions of each object</p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={versioning}
              aria-label="Versioning"
              onClick={() => setVersioning(!versioning)}
              className={`relative w-11 h-6 rounded-full transition-colors ${versioning ? 'bg-ps-accent' : 'bg-ps-strong'}`}
            >
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${versioning ? 'translate-x-5' : ''}`} />
            </button>
          </div>

          <div className="flex items-center justify-between py-1">
            <div>
              <p className="m-0 text-sm text-ps-text">Public Access</p>
              <p className="m-0 text-xs text-ps-subtle">Allow unauthenticated GET requests</p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={isPublic}
              aria-label="Public access"
              onClick={() => setIsPublic(!isPublic)}
              className={`relative w-11 h-6 rounded-full transition-colors ${isPublic ? 'bg-ps-accent' : 'bg-ps-strong'}`}
            >
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${isPublic ? 'translate-x-5' : ''}`} />
            </button>
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="ps-btn2 flex-1"
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={isCreating || !name}
            className="ps-btn flex-1"
          >
            {isCreating ? 'Creating…' : 'Create bucket'}
          </button>
        </div>
      </div>
    </div>
  );
}
